import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import type { RadioGroupProps } from '@qbe/ads-core';
import { Button, FormGroupLabel, RadioGroup, Spinner, Typography } from '@qbe/ads-core';
import React, { useEffect, useMemo, useState } from 'react';

import ExcessErrorModal from '~/components/ExcessErrorModal';
import { ContextualHelpWithButton } from '~/components-ds/ContextualHelp/ContextualHelpWithButton';
import CurrencyField from '~/components-ds/CurrencyField';
import { CURRENCY_TEXTFIELD_LIMIT, THEFT_SECURITY_OPTIONS } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useCoverDetails, useQuote } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useCoverDetailsEffects } from '~/hooks/useCoverDetailsEffects';
import { updateSecurity, updateTheftContentsSI, updateTobaccoSI } from '~/slices/coverDetails';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction, qalFormStartAction } from '~/thunks/analytics';
import { Cover, DynamicCoverQuestionID } from '~/types/cover';
import { dynamicQuestionExists, isTruthy } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

const QuestionContentsSI: React.FC = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsTheft.fields.contentsInput',
  });
  const dispatch = useAppDispatch();
  const { theftContentsSI } = useCoverDetails();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <CurrencyField
      id="theftContentsSI"
      name="theftContentsSI sum insured"
      label={getTextContent('label')}
      helperText={getTextContent('helperText')}
      errorText={getTextContent('invalid')}
      data-track={getTextContent('label')}
      data-track-value="mask"
      defaultValue={theftContentsSI?.toString()}
      fieldValidation={{
        validations: {
          numericOnly: true,
          required: true,
          minValue: 1,
          maxLength: CURRENCY_TEXTFIELD_LIMIT,
        },
        action: updateTheftContentsSI,
      }}
      required={!isTruthy(theftContentsSI)}
      onChange={handleFormStart}
    />
  );
};

const tobaccoSIValidation = {
  validations: {
    numericOnly: true,
    maxLength: CURRENCY_TEXTFIELD_LIMIT,
  },
  action: updateTobaccoSI,
};

interface QuestionTobaccoSIProps {}
const QuestionTobaccoSI: React.FC<QuestionTobaccoSIProps> = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsTheft.fields.tobaccoInput',
  });
  const { tobaccoSI } = useCoverDetails();
  const dispatch = useAppDispatch();
  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <CurrencyField
      id="tobaccoSI"
      name="tobaccoSI sum insured"
      label={getTextContent('label')}
      data-track={getTextContent('label')}
      data-track-value="mask"
      helperText={getTextContent('helperText')}
      errorText={getTextContent('invalid')}
      defaultValue={tobaccoSI?.toString()}
      fieldValidation={tobaccoSIValidation}
      onChange={handleFormStart}
    />
  );
};

interface QuestionSecurityProps {}
const QuestionSecurity: React.FC<QuestionSecurityProps> = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { security } = useCoverDetails();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };
  const radioListOptions = useMemo(
    () =>
      THEFT_SECURITY_OPTIONS.map(({ text, value }) => ({
        label: text,
        id: value,
        value,
        'data-track': 'security',
        'data-track-value': 'capture',
      })) as RadioGroupProps['radioList'],
    []
  );

  return (
    <RadioGroup
      value={security}
      // TODO - remove title and titleHidden attrs - https://qbe-appservices.atlassian.net/browse/AD1-405
      title=""
      titleHidden
      name="security"
      styleVariant="flat"
      direction="vertical"
      radioList={radioListOptions}
      onChange={(value) => {
        handleFormStart();
        dispatch(updateSecurity(value));
        navigate('#next');
      }}
    />
  );
};

const CoverDetailsTheft: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent, getJSXContent } = useAppContent();
  const { getTextContent: getTheftText, getJSXContent: getTheftJsx } = useAppContent({
    basePath: 'dsme.coverDetailsTheft',
  });
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { levelOfExcess } = useQuote();
  const { isValidTheft, theftContentsSI, tobaccoSI, dynamicCoverQuestions } = useCoverDetails();
  const [openExcessError, setOpenExcessError] = useState(false);

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const showTobaccoSI = dynamicQuestionExists(
    dynamicCoverQuestions ?? [],
    Cover.Theft,
    DynamicCoverQuestionID.TOBACCO_SI
  );

  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.Theft,
  });

  /** @clarify is there a soft decline? */
  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    theftContentsSI! < levelOfExcess! || (tobaccoSI && tobaccoSI < levelOfExcess!)
      ? setOpenExcessError(true)
      : quotePremiums();
    handleFormComplete();
  };

  useEffect(() => {
    !isValidTheft
      ? dispatch(removeValidatedCover(Cover.Theft))
      : dispatch(addValidatedCover(Cover.Theft));
  }, [dispatch, isValidTheft]);

  return (
    <>
      <FormCard variant="sectionTitle" title={getTheftText('title')}>
        <FormBlock preset="text_questionHeading">
          <Typography variant="h5" component="legend" textAlign="center">
            {getTheftText('fields.sumInsured.title')}
          </Typography>
        </FormBlock>

        <FormGroupLabel
          alignCenter
          title={getTheftText('fields.contentsInput.title')}
          titleSuffix={
            <ContextualHelpWithButton tracking="help: Contents">
              <Typography>{getTheftJsx('contextualhelpers.contentsInput.description')}</Typography>
            </ContextualHelpWithButton>
          }
        />

        <FormBlock preset="input" maxWidth="100%">
          <QuestionContentsSI />
        </FormBlock>

        {showTobaccoSI && (
          <>
            <FormGroupLabel alignCenter title={getTheftText('fields.tobaccoInput.title')} />

            <FormBlock preset="input" maxWidth="100%">
              <QuestionTobaccoSI />
            </FormBlock>
          </>
        )}

        <FormGroupLabel
          alignCenter
          title={getTheftText('fields.security.title')}
          subtitle={getTheftText('fields.security.description')}
        />

        <FormBlock preset="input" maxWidth="100%">
          <QuestionSecurity />
        </FormBlock>

        <Spinner loading={isLoading} description={getSharedContent('loadingLabel')} />

        <FormBlock preset="button">
          <Button
            id="next"
            data-track={`${buttonText} - theft`}
            onClick={handleNext}
            disabled={!isValidTheft || isLoading}
            label={buttonText}
          />
        </FormBlock>
      </FormCard>

      <ExcessErrorModal
        open={openExcessError}
        onContinue={() => {
          setOpenExcessError(false);
          navigate('#theftContentsSI');
        }}
        onCancel={() => {
          navigate(getTextContent('shared.qbeHome'));
        }}
      >
        <Typography>{getJSXContent('dsme.coverDetails.shared.excessError.description')}</Typography>
      </ExcessErrorModal>
    </>
  );
};

export default CoverDetailsTheft;
